import React, { useState } from "react";
import toast from "react-hot-toast";
import logo from "../assets/logo.png";
import { useNavigate } from "react-router-dom";
import { apiConnectorPost } from "../utils/APIConnector";
import { endpoint } from "../utils/APIRoutes";
import Swal from "sweetalert2";
import Loader from "../Shared/Loader";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const forgotemailFn = async () => {
    try {
      setLoading(true);
      const res = await apiConnectorPost(endpoint?.forgot_password, {
        usermail: email,
      });
      if (res?.data?.success) {
        Swal.fire({
          title: "Success",
          text: res.data.message,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/login");
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#060E1C] relative overflow-hidden">
      <Loader isLoading={loading} />

      {/* Animated background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(37,99,235,0.12),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(37,99,235,0.08),transparent_50%)]"></div>
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl animate-pulse"></div>
      <div
        className="absolute bottom-0 right-0 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="absolute top-1/2 left-1/4 w-64 h-64 bg-[#2563EB]/5 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: "2s" }}
      ></div>

      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(37,99,235,0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(37,99,235,0.12) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      ></div>

      {/* Floating particles */}
      <div className="absolute top-20 left-20 w-2 h-2 bg-[#2563EB] rounded-full opacity-60 animate-ping"></div>
      <div
        className="absolute bottom-40 right-32 w-2 h-2 bg-[#22D3EE] rounded-full opacity-60 animate-ping"
        style={{ animationDelay: "1.5s" }}
      ></div>
      <div
        className="absolute top-1/2 left-40 w-1 h-1 bg-[#2563EB] rounded-full opacity-60 animate-ping"
        style={{ animationDelay: "2.5s" }}
      ></div>
      <div
        className="absolute top-1/3 right-1/4 w-1 h-1 bg-[#1D4ED8] rounded-full opacity-50 animate-ping"
        style={{ animationDelay: "3s" }}
      ></div>
      <div
        className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-[#22D3EE] rounded-full opacity-40 animate-ping"
        style={{ animationDelay: "3.5s" }}
      ></div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md mx-1">
        <div className="absolute -inset-1 bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] rounded-3xl opacity-25 blur-xl animate-pulse"></div>
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-3xl opacity-30 blur-lg"></div>

        <div className="relative bg-gradient-to-br from-[#060E1C] via-[#080F1C] to-[#0B1E36] border border-[#2563EB]/40 rounded-3xl px-2 py-12 shadow-2xl shadow-[#2563EB]/25 overflow-hidden backdrop-blur-sm">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#2563EB]/8 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#1D4ED8]/8 rounded-full blur-2xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-[#22D3EE]/5 rounded-full blur-3xl"></div>

          <div className="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2 border-[#2563EB]/25 rounded-tr-3xl"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 border-b-2 border-l-2 border-[#2563EB]/15 rounded-bl-3xl"></div>

          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] animate-pulse"></div>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#2563EB]/40 to-transparent"></div>

          <div className="relative z-10">
            {/* Logo */}
            {/* Enhanced Logo */}
            <div className="flex justify-center mb-8">
              <div className="relative group">
                {/* Enhanced logo glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#22D3EE]/40 to-[#2563EB]/40 rounded-2xl blur-3xl animate-pulse group-hover:scale-110 transition-transform duration-500"></div>
                <div
                  className="absolute inset-0 bg-[#2563EB]/20 rounded-2xl blur-xl animate-pulse"
                  style={{ animationDelay: "0.5s" }}
                ></div>

                {/* Logo container */}
                <div
                  onClick={() => navigate("/")}
                  className="relative flex items-center gap-3 cursor-pointer group-hover:scale-105 transition-all duration-500"
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#22D3EE]/20 to-[#1D4ED8]/20 border border-[#2563EB]/50 shadow-[0_0_20px_rgba(37,99,235,0.25)] flex items-center justify-center overflow-hidden">
                    <img
                      src={logo}
                      alt="logo"
                      className="w-7 h-7 object-contain relative z-10 drop-shadow-lg"
                    />
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-base font-extrabold tracking-[0.05em] text-[#f3f4f6]">
                      HEXAFUND
                    </span>
                    <span className="mt-1 text-[10px] tracking-[0.14em] uppercase text-[#2563EB]">
                      Crowd Funding
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Heading */}
            <div className="text-center mb-10">
              <h2 className="text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] text-3xl font-bold mb-3 tracking-wide">
                Forgot Password
              </h2>
              <p className="text-[#7DD3FC] text-base">
                Enter your email to receive a password reset link
              </p>
              <div className="flex items-center justify-center gap-3 mt-6">
                <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#2563EB]/60"></div>
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#22D3EE] to-[#2563EB] animate-pulse"></div>
                <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#2563EB]/60"></div>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                forgotemailFn();
              }}
            >
              <div className="mb-8">
                <input
                  placeholder="Enter Email"
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-[#071526]/80 border border-[#2563EB]/40 text-sm text-[#E2E8F0] placeholder-[#64748B] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB]"
                  required
                />
              </div>

              {/* Info Box */}
              <div className="mb-8 flex items-start gap-4 bg-gradient-to-r from-[#071526]/50 via-[#0B1E36]/40 to-[#071526]/50 backdrop-blur-sm border border-[#2563EB]/40 rounded-xl p-5 hover:border-[#2563EB]/60 transition-all duration-300">
                <div className="p-2 bg-[#2563EB]/20 rounded-lg">
                  <svg
                    className="w-5 h-5 text-[#2563EB]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-[#7DD3FC] font-semibold text-sm mb-1">
                    Secure Reset
                  </h4>
                  <p className="text-[#94A3B8] text-xs leading-relaxed">
                    We will send a reset link to your registered email address.
                  </p>
                </div>
              </div>

              {/* Send Button */}
              <button
                type="submit"
                className="relative w-full py-5 rounded-2xl font-bold text-lg overflow-hidden group transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 mb-8"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#1D4ED8] via-[#2563EB] to-[#22D3EE]"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-[#22D3EE] via-[#2563EB] to-[#1D4ED8] opacity-0 group-hover:opacity-90 transition-opacity duration-500"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-[#22D3EE] to-[#2563EB] opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl rounded-2xl"></div>
                <span className="relative z-10 flex items-center justify-center gap-4 text-white">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 17l-4-4m0 0l4-4m-4 4H4m16 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Send Password
                </span>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 translate-x-[-300%] group-hover:translate-x-[300%] transition-transform duration-1000"></div>
                </div>
              </button>
            </form>

            {/* Go to Login */}
            <div className="text-center mb-6">
              <p className="text-gray-300 text-sm">
                Go To
                <span
                  className="text-transparent bg-clip-text bg-gradient-to-r from-[#22D3EE] to-[#2563EB] font-bold cursor-pointer hover:from-[#2563EB] hover:to-[#1D4ED8] transition-all duration-300 inline-flex items-center gap-2 hover:scale-105 ml-2"
                  onClick={() => navigate("/login")}
                >
                  Login ?
                  <svg
                    className="w-4 h-4 text-[#2563EB]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </p>
            </div>

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-3 text-gray-400 text-xs bg-[#0B1E36]/50 backdrop-blur-sm rounded-full px-4 py-3 border border-[#132440]/60">
              <div className="p-1 bg-[#2563EB]/20 rounded-full">
                <svg
                  className="w-4 h-4 text-[#2563EB]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <span className="font-medium">
                Secured with blockchain technology
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
